import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import Layout from '../components/Layout';
import useAggiornamentoPeriodico from '../hooks/useAggiornamentoPeriodico';
import { formattaData } from '../utils/date';

const formatta = d => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

// Cestino: record eliminati. Il server decide cosa si vede e cosa si può ripristinare, per sezione di permesso (sezioni_visibili, ripristinabili);
// qui si mostra soltanto: una sezione non visibile non compare né con il titolo né con il conteggio, senza "Ripristina" se manca la scrittura.
const TUTTE = ['aziende', 'lavoratori', 'competenze', 'clienti_studio', 'diciture', 'scadenze', 'scadenze_tipi', 'scadenze_categorie', 'codici_unilav', 'livelli_ccnl', 'fatturanti', 'listino_prezzi'];
export default function Cestino() {
  const [dati, setDati] = useState({ aziende: [], lavoratori: [], competenze: [], clienti_studio: [], diciture: [], scadenze: [], scadenze_anagrafiche: [], sezioni_visibili: [], ripristinabili: [], caricato: false });
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(null);

  const carica = useCallback(async () => {
    try {
      const res = await api.get('/cestino');
      // Senza l'elenco dei permessi nella risposta (server più vecchio) si mostra tutto, come prima
      setDati({ aziende: [], lavoratori: [], competenze: [], clienti_studio: [], diciture: [], scadenze: [], scadenze_anagrafiche: [],
        sezioni_visibili: TUTTE, ripristinabili: TUTTE, ...res.data, caricato: true });
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Errore nel caricamento del cestino');
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);
  useAggiornamentoPeriodico(carica);

  const ripristina = async (tipo, id, nome) => {
    if (!window.confirm(`Ripristinare ${nome}?`)) return;
    setInCorso(`${tipo}-${id}`);
    setErrore('');
    try {
      const res = await api.post(`/cestino/${tipo}/${id}/ripristina`);
      carica();
      if (tipo === 'aziende') {
        // L'azienda ripristinata riattiva gli account disattivati insieme a lei, ma la copia cifrata delle password non torna
        const n = res?.data?.account_riattivati || 0;
        window.alert(`Azienda ripristinata. ${n === 0 ? 'Nessun account da riattivare.' : `Account riattivati: ${n}: gli utenti possono accedere di nuovo con la loro password.`}`
          + ` La copia della password non viene ripristinata: per rivederla con «Mostra password» imposta o reimposta la password da Impostazioni → Utenti.`);
      }
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Ripristino non riuscito');
    } finally {
      setInCorso(null);
    }
  };

  const visibile = (...entita) => entita.some(e => dati.sezioni_visibili.includes(e));
  const puoRipristinare = (entita) => dati.ripristinabili.includes(entita);
  // Pulsante "Ripristina": assente se manca la scrittura sulla sezione del record
  const bottone = (entita, id, nome, ok = true) => (puoRipristinare(entita) && ok
    ? <button onClick={() => ripristina(entita, id, nome)} disabled={inCorso === `${entita}-${id}`} style={styles.btnSmall}>Ripristina</button>
    : null);
  const nessunaSezione = dati.caricato && dati.sezioni_visibili.length === 0;

  return (
    <Layout>
      <h2 style={{ marginTop: 0 }}>Cestino</h2>
      <p style={{ color: '#666', marginTop: '-0.5rem' }}>Record eliminati o disattivati. Il ripristino li rimette negli elenchi.</p>
      {errore && <div style={styles.errore}>{errore}</div>}
      {nessunaSezione && <p style={{ color: '#666' }}>Non hai accesso a nessuna sezione del Cestino.</p>}

      {visibile('aziende') && <>
      <h3>Aziende di Intermittenti ({dati.aziende.length})</h3>
      <Tabella
        colonne={['Ragione sociale', 'Codice fiscale', 'Eliminato il', 'Da']}
        righe={dati.aziende.map(a => ({
          key: `a-${a.id}`,
          celle: [a.ragione_sociale, a.codice_fiscale, formatta(a.eliminato_il), a.eliminato_da_nome || '—'],
          azione: bottone('aziende', a.id, a.ragione_sociale, a.ripristinabile !== false)
            || (puoRipristinare('aziende') ? <small style={{ color: '#999' }}>Servono anche i permessi su «Account clienti»</small> : null),
        }))}
      />
      </>}

      {visibile('lavoratori') && <>
      <h3>Lavoratori ({dati.lavoratori.length})</h3>
      <Tabella
        colonne={['Lavoratore', 'Codice fiscale', 'Azienda', 'Eliminato il', 'Da']}
        righe={dati.lavoratori.map(l => ({
          key: `l-${l.id}`,
          celle: [`${l.cognome} ${l.nome}`, l.codice_fiscale, l.ragione_sociale, formatta(l.eliminato_il), l.eliminato_da_nome || '—'],
          azione: l.azienda_eliminata
            ? <small style={{ color: '#999' }}>Ripristina prima l'azienda</small>
            : bottone('lavoratori', l.id, `${l.cognome} ${l.nome}`),
        }))}
      />
      </>}

      {visibile('competenze') && <>
      <h3>Parcellazione ({dati.competenze.length})</h3>
      <Tabella
        colonne={['Data', 'Azienda', 'Descrizione', 'Eliminata il', 'Da']}
        righe={dati.competenze.map(c => ({
          key: `c-${c.id}`,
          celle: [formattaData(c.data), c.cliente, c.descrizione, formatta(c.eliminato_il), c.eliminato_da_nome || '—'],
          azione: c.cliente_eliminato
            ? <small style={{ color: '#999' }}>Ripristina prima l'azienda</small>
            : bottone('competenze', c.id, `la prestazione "${c.descrizione}"`),
        }))}
      />
      </>}

      {visibile('clienti_studio') && <>
      <h3>Aziende di Parcellazione e Scadenziario ({dati.clienti_studio.length})</h3>
      <Tabella
        colonne={['Ragione sociale', 'Eliminato il', 'Da']}
        righe={dati.clienti_studio.map(c => ({
          key: `cs-${c.id}`,
          celle: [c.ragione_sociale, formatta(c.eliminato_il), c.eliminato_da_nome || '—'],
          azione: bottone('clienti_studio', c.id, c.ragione_sociale),
        }))}
      />
      </>}

      {visibile('diciture') && <>
      <h3>Diciture ({dati.diciture.length})</h3>
      <Tabella
        colonne={['Testo', 'Eliminata il', 'Da']}
        righe={dati.diciture.map(d => ({
          key: `d-${d.id}`,
          celle: [d.testo, formatta(d.eliminato_il), d.eliminato_da_nome || '—'],
          azione: bottone('diciture', d.id, `la dicitura "${d.testo}"`),
        }))}
      />
      </>}

      {visibile('scadenze') && <>
      <h3>Scadenziario ({dati.scadenze.length})</h3>
      <Tabella
        colonne={['Azienda', 'Evento', 'Tipo / categoria', 'Descrizione', 'Eliminata il', 'Da']}
        righe={dati.scadenze.map(s => ({
          key: `s-${s.id}`,
          celle: [s.ditta, formattaData(s.data_evento), [s.tipo, s.categoria].filter(Boolean).join(' · '), s.lavoratore || '', formatta(s.eliminato_il), s.eliminato_da_nome || '—'],
          azione: s.ditta_eliminata
            ? <small style={{ color: '#999' }}>Ripristina prima l'azienda</small>
            : bottone('scadenze', s.id, `la voce dello Scadenziario di ${s.ditta}`),
        }))}
      />
      </>}

      {visibile('scadenze_tipi', 'scadenze_categorie', 'codici_unilav', 'livelli_ccnl', 'fatturanti', 'listino_prezzi') && <>
      <h3>Tipi, categorie, codici, livelli, fatturanti e listino ({dati.scadenze_anagrafiche.length})</h3>
      <Tabella
        colonne={['Genere', 'Nome', 'Eliminato il', 'Da']}
        righe={dati.scadenze_anagrafiche.map(a => ({
          key: `${a.entita}-${a.id}`,
          celle: [a.genere, a.nome, formatta(a.eliminato_il), a.eliminato_da_nome || '—'],
          azione: bottone(a.entita, a.id, `"${a.nome}"`),
        }))}
      />
      </>}
    </Layout>
  );
}

function Tabella({ colonne, righe }) {
  return (
    <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
      <table style={styles.table}>
        <thead>
          <tr>
            {colonne.map(c => <th key={c} style={styles.th}>{c}</th>)}
            <th style={styles.th}>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {righe.map(r => (
            <tr key={r.key} style={styles.tr}>
              {r.celle.map((c, i) => <td key={i} style={styles.td}>{c}</td>)}
              <td style={styles.td}>{r.azione}</td>
            </tr>
          ))}
          {righe.length === 0 && (
            <tr><td colSpan={colonne.length + 1} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>Niente nel cestino</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '0.8rem', background: '#f8f9fa', borderBottom: '2px solid #eee', fontSize: '0.85rem', color: '#555' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '0.7rem 0.8rem', fontSize: '0.9rem' },
  btnSmall: { background: '#eef', color: '#232323', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  errore: { background: '#fee', color: '#c00', padding: '0.7rem', borderRadius: '6px', marginBottom: '1rem' },
};
